import * as THREE from 'three';
import { VoxelGrid } from '../core/voxel-grid.js';
import { createVoxelMaterial } from '../core/materials.js';
import { mulberry32 } from '../core/rng.js';

// ---------------------------------------------------------------------------
// Dutch cumulus. In a country with no hills, the sky does the scenery, so
// these are deliberately oversized: flat-bottomed, piled-up, slow.
//
// A handful of blob shapes are meshed once and then reused across the sky,
// which keeps the whole cloudscape at four geometries.
// ---------------------------------------------------------------------------

function buildCloudShape(seed, blockId) {
    const rng = mulberry32(seed);
    const W = 26;
    const H = 12;
    const D = 18;
    const grid = new VoxelGrid(W, H, D);

    // A few overlapping ellipsoids sitting on a common flat base.
    const lobes = 4 + Math.floor(rng() * 3);
    for (let i = 0; i < lobes; i++) {
        const cx = 5 + rng() * (W - 10);
        const cz = 4 + rng() * (D - 8);
        const rx = 3.5 + rng() * 4.5;
        const rz = 3.0 + rng() * 3.5;
        const ry = 2.5 + rng() * 4.0;
        const cy = 2 + rng() * 2.5;

        for (let y = 0; y < H; y++) {
            for (let z = 0; z < D; z++) {
                for (let x = 0; x < W; x++) {
                    const dx = (x - cx) / rx;
                    const dy = (y - cy) / ry;
                    const dz = (z - cz) / rz;
                    if (dx * dx + dy * dy + dz * dz > 1) continue;
                    if (y < 2) continue;             // flat bottom
                    if (rng() < 0.03) continue;      // nibbled edges
                    grid.set(x, y, z, blockId);
                }
            }
        }
    }
    return grid;
}

export function createClouds(env, palette, { voxelSize, count = 26, seed = 7 }) {
    const cloudId = palette.add('cloud', 0xf2f4f6, { jitter: 0.025 });
    const material = createVoxelMaterial(env);

    // Clouds get their own fog range instead of the shared one. They sit 45 to
    // 175 units out, which the ground fog curve would dissolve into a grey
    // smear — and the whole point of voxel cumulus is that you can see the
    // blocks. Everything else still reads from the environment's uniforms.
    material.uniforms.uFogRange = { value: new THREE.Vector2(260, 620) };
    const group = new THREE.Group();
    group.name = 'clouds';

    const shapes = [];
    for (let i = 0; i < 4; i++) {
        const grid = buildCloudShape(seed + i * 131, cloudId);
        shapes.push(
            grid.build(palette, {
                voxelSize,
                origin: new THREE.Vector3(-13 * voxelSize, 0, -9 * voxelSize),
            }),
        );
    }

    const rng = mulberry32(seed);
    const drift = [];

    // Four shapes shared between eighteen clouds, so they are four instanced
    // meshes rather than eighteen separate draws. Each cloud still keeps its
    // own place, size, spin and speed — only the geometry is shared, which it
    // already was.
    const perShape = shapes.map(() => []);
    for (let i = 0; i < count; i++) {
        const shape = i % shapes.length;
        // Spread across the whole sea now, not the one island it used to be.
        const radius = 25 + rng() * 150;
        const angle = rng() * Math.PI * 2;
        perShape[shape].push({
            position: new THREE.Vector3(
                Math.cos(angle) * radius,
                // High enough to sit above the boat and read as sky, low
                // enough to be in shot from the water. They were pushed up to
                // seventy while the real problem was that instancing had them
                // all stacked at the origin.
                44 + rng() * 26,
                Math.sin(angle) * radius,
            ),
            scale: 1.6 + rng() * 2.8,
            spin: rng() * Math.PI * 2,
            speed: 0.35 + rng() * 0.5,
        });
    }

    const dummy = new THREE.Object3D();
    shapes.forEach((geometry, i) => {
        const clouds = perShape[i];
        if (!clouds.length) return;
        const mesh = new THREE.InstancedMesh(geometry, material, clouds.length);
        mesh.name = 'clouds';
        // They wander the whole sky; culling them by their origin pops them.
        mesh.frustumCulled = false;
        group.add(mesh);
        clouds.forEach((c, k) => drift.push({ ...c, mesh, index: k }));
    });

    // Clouds blow west to east and wrap around when they leave the map.
    const BOUND = 210;
    const place = (d) => {
        dummy.position.copy(d.position);
        dummy.rotation.set(0, d.spin, 0);
        dummy.scale.setScalar(d.scale);
        dummy.updateMatrix();
        d.mesh.setMatrixAt(d.index, dummy.matrix);
    };
    for (const d of drift) place(d);
    for (const g of group.children) g.instanceMatrix.needsUpdate = true;

    group.userData.update = (dt) => {
        for (const d of drift) {
            d.position.x += d.speed * dt;
            if (d.position.x > BOUND) d.position.x = -BOUND;
            place(d);
        }
        for (const g of group.children) g.instanceMatrix.needsUpdate = true;
    };

    return group;
}
